/**
 * Clara360 - Bank Report Generator
 * 
 * Generates professional bank reports for properties including:
 * - Property details
 * - Owner information (Eigentümerstruktur)
 * - Financial metrics
 * - Tenant structure
 * - AI-generated analysis
 * 
 * @version 1.1.0
 * @author Manus AI
 * @date 2025-10-21
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  ArrowLeft, 
  FileText, 
  Download, 
  Building, 
  User, 
  Mail, 
  Phone,
  MapPin,
  TrendingUp,
  Users,
  Home,
  AlertCircle,
  CheckCircle,
  Loader,
  Database,
  Calculator,
  Sparkles,
  Presentation
} from 'lucide-react';
import { masterSupabase } from '../lib/supabase-master';
import BankReportSlides from '../components/BankReportSlides';
import EmailShareButton from '../components/BankReportInteractive/controls/EmailShareButton';
import ReportHistory from '../components/BankReportInteractive/controls/ReportHistory';
import { jsPDF } from 'jspdf';

export default function BankReport() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // State
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [loadingProperties, setLoadingProperties] = useState(true);
  const [showSlides, setShowSlides] = useState(false);
  const [slides, setSlides] = useState(null);
  const [aiProvider, setAiProvider] = useState('qwen'); // Qwen2.5 as primary // AI provider selection
  const [showHistoryModal, setShowHistoryModal] = useState(false); // History modal state
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false); // WeasyPrint PDF generation state
  
  // Progress tracking
  const [progress, setProgress] = useState(0);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [estimatedTime, setEstimatedTime] = useState(0);

  // Load properties with owner info on mount
  useEffect(() => {
    loadProperties();
  }, []);

  /**
   * Load all properties with owner information
   */
  const loadProperties = async () => {
    try {
      setLoadingProperties(true);
      
      // Check if user is available
      if (!user?.id) {
        console.log('[BANKREPORT] No user ID available');
        setProperties([]);
        setLoadingProperties(false);
        return;
      }
      
      console.log('[BANKREPORT] Loading properties for user:', user.id);
      
      const { data, error } = await masterSupabase
        .from('properties')
        .select('*, owners:owner_id (id, name)')
        .eq('created_by', user.id)
        .order('property_name');
      
      if (error) {
        console.error('Error loading properties:', error);
        setError('Fehler beim Laden der Objekte');
        return;
      }
      
      console.log('[BANKREPORT] Loaded properties:', data?.length || 0);
      setProperties(data || []);
    } catch (err) {
      console.error('Error in loadProperties:', err);
      setError('Fehler beim Laden der Objekte');
    } finally {
      setLoadingProperties(false);
    }
  };

  /**
   * Handle property selection
   */
  const handlePropertySelect = (propertyId) => {
    if (!propertyId || propertyId === '') {
      setSelectedProperty(null);
      setReport(null);
      setError(null);
      return;
    }
    
    const property = properties.find(p => p.id === propertyId);
    console.log('[BANKREPORT] Selected property:', property?.property_name);
    setSelectedProperty(property);
    setReport(null);
    setError(null);
  };

  /**
   * Generate interactive slides presentation
   */
  const handleGenerateInteractiveReport = async () => {
    if (!selectedProperty) {
      setError('Bitte wählen Sie ein Objekt aus');
      return;
    }
    
    setLoading(true);
    setError(null);
    setReport(null);
    setSlides(null);
    setProgress(0);
    setCurrentStepIndex(0);
    setEstimatedTime(25);
    
    // Progress simulation steps with detailed info
    const steps = [
      { progress: 10, icon: Database, text: 'Lade Objektdaten', subtext: 'Immobilie, Einheiten, Eigentümer', duration: 2000 },
      { progress: 20, icon: Users, text: 'Lade Mieter & Verträge', subtext: 'Aktive Mietverhältnisse analysieren', duration: 2000 },
      { progress: 30, icon: TrendingUp, text: 'Lade Finanzierungen', subtext: 'Kredite, LTV, DSCR berechnen', duration: 2000 },
      { progress: 40, icon: Calculator, text: 'Berechne VPI-Anpassungen', subtext: 'Mietanpassungspotenzial ermitteln', duration: 3000 },
      { progress: 60, icon: Sparkles, text: 'KI-Analyse läuft', subtext: 'Gemini 2.0 Flash generiert Insights', duration: 8000 },
      { progress: 80, icon: Presentation, text: 'Erstelle Slides', subtext: 'Charts und Visualisierungen', duration: 4000 },
      { progress: 95, icon: CheckCircle, text: 'Finalisiere', subtext: 'Präsentation wird vorbereitet', duration: 2000 }
    ];
    
    const startTime = Date.now();
    const totalDuration = steps.reduce((sum, step) => sum + step.duration, 0);
    
    try {
      console.log('[BANKREPORT] Generating interactive report for:', selectedProperty.property_name);
      
      // Simulate progress
      let currentStep = 0;
      const progressInterval = setInterval(() => {
        if (currentStep < steps.length) {
          const step = steps[currentStep];
          setProgress(step.progress);
          setCurrentStepIndex(currentStep);
          
          const elapsed = Date.now() - startTime;
          const remaining = Math.max(0, Math.ceil((totalDuration - elapsed) / 1000));
          setEstimatedTime(remaining);
          
          currentStep++;
        }
      }, 2500);
      
      const response = await fetch('https://api.clara360.de/api/reports/interactive/generate', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          propertyId: selectedProperty.id,
          propertyName: selectedProperty.property_name,
          userId: user.id,
          aiProvider: aiProvider
        })
      });
      
      clearInterval(progressInterval);
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Fehler beim Erstellen der Präsentation');
      }
      
      // Final progress
      setProgress(100);
      setCurrentStepIndex(steps.length - 1);
      setEstimatedTime(0);
      
      setTimeout(() => {
        console.log('[BANKREPORT] Interactive report generated:', data.slides.length, 'slides');
        setSlides(data.slides);
        setShowSlides(true);
      }, 500);
    } catch (err) {
      console.error('[BANKREPORT] Error generating interactive report:', err);
      setError(err.message || 'Fehler beim Erstellen der Präsentation');
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  };

  /**
   * Generate bank report via API (text version)
   */
  const handleGenerateReport = async () => {
    if (!selectedProperty) {
      setError('Bitte wählen Sie ein Objekt aus');
      return;
    }
    
    setLoading(true);
    setError(null);
    setReport(null);
    
    try {
      console.log('[BANKREPORT] Generating report for:', selectedProperty.id);
      
      const response = await fetch('https://api.clara360.de/api/reports/bank', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: user.id,
          propertyId: selectedProperty.id
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Fehler beim Erstellen des Reports');
      }
      
      console.log('[BANKREPORT] Report generated successfully');
      setReport(data);
    } catch (err) {
      console.error('[BANKREPORT] Error generating report:', err);
      setError(err.message || 'Fehler beim Erstellen des Bankreports');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Download report as professional PDF using WeasyPrint
   */
  const handleDownloadPDF = async () => {
    if (!report || !selectedProperty) return;
    
    try {
      setIsGeneratingPDF(true);
      console.log('[PDF Export] Starting WeasyPrint PDF generation');
      
      // Prepare HTML with light mode CSS for PDF
      const html = `
        <!DOCTYPE html>
        <html lang="de">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Bankreport - ${selectedProperty.property_name}</title>
        </head>
        <body>
          <div class="report-container">
            <h1>Bankreport</h1>
            <h2>${selectedProperty.property_name}</h2>
            ${selectedProperty.address ? `<p class="address">${selectedProperty.address}</p>` : ''}
            <hr>
            ${report.report}
          </div>
        </body>
        </html>
      `;
      
      // CSS for professional print styling (light mode)
      const css = `
        @page {
          size: A4;
          margin: 2cm;
        }
        
        body {
          font-family: Arial, Helvetica, sans-serif;
          font-size: 11pt;
          line-height: 1.6;
          color: #333333;
          background: white;
        }
        
        .report-container {
          max-width: 100%;
        }
        
        h1 {
          font-size: 24pt;
          font-weight: bold;
          color: #1a1a1a;
          margin-bottom: 0.5em;
          border-bottom: 3px solid #4263eb;
          padding-bottom: 0.3em;
        }
        
        h2 {
          font-size: 18pt;
          font-weight: bold;
          color: #2c2c2c;
          margin-top: 1em;
          margin-bottom: 0.5em;
        }
        
        h3 {
          font-size: 14pt;
          font-weight: bold;
          color: #3c3c3c;
          margin-top: 0.8em;
          margin-bottom: 0.4em;
        }
        
        .address {
          font-size: 12pt;
          color: #666666;
          margin-bottom: 1em;
        }
        
        hr {
          border: none;
          border-top: 1px solid #e0e0e0;
          margin: 1.5em 0;
        }
        
        p {
          margin: 0.5em 0;
          text-align: justify;
        }
        
        ul, ol {
          margin: 0.5em 0;
          padding-left: 2em;
        }
        
        li {
          margin: 0.3em 0;
        }
        
        table {
          width: 100%;
          border-collapse: collapse;
          margin: 1em 0;
        }
        
        th, td {
          padding: 0.5em;
          text-align: left;
          border-bottom: 1px solid #e0e0e0;
        }
        
        th {
          background-color: #f5f5f5;
          font-weight: bold;
          color: #1a1a1a;
        }
        
        /* Status indicators - ensure visibility in print */
        .status-indicator {
          display: inline-block;
          width: 12px;
          height: 12px;
          border-radius: 2px;
          margin-right: 6px;
          vertical-align: middle;
        }
        
        .status-green { background-color: #10b981; }
        .status-orange { background-color: #f59e0b; }
        .status-red { background-color: #ef4444; }
        .status-gray { background-color: #6b7280; }
        
        /* Remove any dark mode styles */
        * {
          background-color: transparent !important;
        }
        
        body {
          background-color: white !important;
        }
      `;
      
      console.log('[PDF Export] HTML length:', html.length);
      console.log('[PDF Export] CSS length:', css.length);
      
      // Call WeasyPrint backend
      const response = await fetch('/api/pdf/weasyprint/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          html: html,
          css: css,
          filename: `Bankreport_${selectedProperty.property_name.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate PDF');
      }
      
      // Download PDF
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Bankreport_${selectedProperty.property_name.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      console.log('[PDF Export] PDF downloaded successfully');
      setIsGeneratingPDF(false);
    } catch (error) {
      console.error('[PDF Export] Error:', error);
      setIsGeneratingPDF(false);
      alert('Fehler beim Erstellen des PDFs: ' + error.message);
    }
  };

  /**
   * Download report as professional PDF with HTML-DOM-based parsing (jsPDF)
   */
  const handleDownloadReport = () => {
    if (!report) return;
    
    try {
      // Create PDF document (A4 format)
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (2 * margin);
      let yPosition = margin;
      
      // === HEADER FUNCTION (for all pages) ===
      const addPageHeader = () => {
        doc.setFillColor(66, 99, 235); // Blue header
        doc.rect(0, 0, pageWidth, 35, 'F');
        
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 255, 255);
        doc.text('BANKREPORT', margin, 15);
        
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(`${selectedProperty.property_name} | ${new Date().toLocaleDateString('de-DE')}`, margin, 25);
        
        return 45; // Return starting yPosition after header
      };
      
      // Helper to check page break
      const checkPageBreak = (requiredSpace) => {
        if (yPosition + requiredSpace > pageHeight - margin - 15) {
          doc.addPage();
          yPosition = addPageHeader();
        }
      };
      
      // Helper function to add text with word wrap
      const addText = (text, fontSize, fontStyle = 'normal', color = [0, 0, 0]) => {
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', fontStyle);
        doc.setTextColor(...color);
        const lines = doc.splitTextToSize(text, contentWidth);
        
        checkPageBreak(lines.length * fontSize * 0.35 + 3);
        
        doc.text(lines, margin, yPosition);
        yPosition += lines.length * fontSize * 0.35 + 3;
      };
      
      // Add horizontal line
      const addLine = () => {
        doc.setDrawColor(200, 200, 200);
        doc.line(margin, yPosition, pageWidth - margin, yPosition);
        yPosition += 5;
      };
      
      // Add header to first page
      yPosition = addPageHeader();
      
      // === PROPERTY INFO ===
      addText(selectedProperty.property_name, 18, 'bold', [66, 99, 235]);
      
      if (selectedProperty.address) {
        addText(selectedProperty.address, 11, 'normal', [100, 100, 100]);
      }
      
      yPosition += 5;
      addLine();
      yPosition += 5;
      
      // === KEY METRICS TABLE ===
      addText('KENNZAHLEN', 14, 'bold');
      yPosition += 2;
      
      // Draw table
      const tableData = [
        ['Jahresmiete', `${parseFloat(report.metrics.yearlyRent).toLocaleString('de-DE')} €`],
        ['Bruttorendite', `${report.metrics.grossYield}%`],
        ['Auslastung', `${report.metrics.occupancyRate}%`],
        ['Aktive Mieter', `${report.metrics.activeTenantCount} von ${report.metrics.totalUnitCount}`],
        ['Ø Miete/m²', `${report.metrics.avgRentPerSqm} €`]
      ];
      
      const rowHeight = 8;
      const col1Width = contentWidth * 0.5;
      const col2Width = contentWidth * 0.5;
      
      tableData.forEach((row, index) => {
        checkPageBreak(rowHeight);
        
        // Alternate row colors
        if (index % 2 === 0) {
          doc.setFillColor(245, 247, 250);
          doc.rect(margin, yPosition - 5, contentWidth, rowHeight, 'F');
        }
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(60, 60, 60);
        doc.text(row[0], margin + 2, yPosition);
        
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(row[1], margin + col1Width + 2, yPosition);
        
        yPosition += rowHeight;
      });
      
      yPosition += 5;
      addLine();
      yPosition += 5;
      
      // === OWNER INFO ===
      if (selectedProperty.owners) {
        addText('EIGENTÜMER', 14, 'bold');
        yPosition += 2;
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(60, 60, 60);
        doc.text(`Name: ${selectedProperty.owners.name}`, margin, yPosition);
        yPosition += 6;
        
        yPosition += 5;
        addLine();
        yPosition += 5;
      }
      
      // === AI ANALYSIS ===
      addText('KI-ANALYSE', 14, 'bold');
      yPosition += 2;
      
      // Helper to add section header
      const addSectionHeader = (title) => {
        checkPageBreak(15);
        yPosition += 5;
        
        // Background bar
        doc.setFillColor(240, 242, 245);
        doc.rect(margin - 5, yPosition - 6, contentWidth + 10, 10, 'F');
        
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(66, 99, 235);
        doc.text(title.toUpperCase(), margin, yPosition);
        yPosition += 10;
      };
      
      // Helper to add data table
      const addDataTable = (data) => {
        const rowHeight = 7;
        const col1Width = contentWidth * 0.45;
        const col2Width = contentWidth * 0.55;
        
        data.forEach((row, index) => {
          checkPageBreak(rowHeight);
          
          // Alternate row colors
          if (index % 2 === 0) {
            doc.setFillColor(248, 249, 250);
            doc.rect(margin, yPosition, contentWidth, rowHeight, 'F');
          }
          
          doc.setFontSize(9);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(60, 60, 60);
          doc.text(row[0], margin + 2, yPosition + 5);
          
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(0, 0, 0);
          const valueLines = doc.splitTextToSize(String(row[1]), col2Width - 4);
          doc.text(valueLines, margin + col1Width + 2, yPosition + 5);
          
          yPosition += rowHeight;
        });
        
        yPosition += 3;
      };
      
      // === SIMPLE TEXT PARSER WITH TABLE DETECTION ===
      const parseTextContent = (htmlString) => {
        // Strip HTML tags but preserve structure
        let text = htmlString;
        
        // Remove style and script tags
        text = text.replace(/<style[^>]*>.*?<\/style>/gis, '');
        text = text.replace(/<script[^>]*>.*?<\/script>/gis, '');
        
        // Add line breaks for block elements
        text = text.replace(/<\/(div|p|h[1-6]|li|tr|br)>/gi, '\n');
        text = text.replace(/<br\s*\/?>/gi, '\n');
        
        // Remove all remaining HTML tags
        text = text.replace(/<[^>]+>/g, '');
        
        // Decode HTML entities
        const tmp = document.createElement('textarea');
        tmp.innerHTML = text;
        text = tmp.value;
        
        // Clean up whitespace
        const lines = text
          .split('\n')
          .map(line => line.trim())
          .filter(line => line.length > 0);
        
        return lines;
      };
      
      // Parse and render content
      const lines = parseTextContent(report.report);
      
      // Group lines into sections
      const sections = [];
      let currentSection = { title: 'Analyse', content: [] };
      
      lines.forEach(line => {
        // Detect section headers (short lines, all caps, or ending with colon)
        const isHeader = line.length < 60 && (
          /^[A-ZÄÖÜ\s\d]+$/.test(line) ||
          line.endsWith(':') ||
          /^\d+\.\s+[A-ZÄÖÜ]/.test(line)
        );
        
        if (isHeader) {
          if (currentSection.content.length > 0) {
            sections.push(currentSection);
          }
          currentSection = { title: line.replace(':', '').trim(), content: [] };
        } else {
          currentSection.content.push(line);
        }
      });
      
      if (currentSection.content.length > 0) {
        sections.push(currentSection);
      }
      
      // Render sections
      sections.forEach(section => {
        addSectionHeader(section.title);
        
        const tableRows = [];
        const paragraphs = [];
        
        // Process content lines
        let i = 0;
        while (i < section.content.length) {
          const line = section.content[i];
          
          // Method 1: Colon-separated key-value
          if (line.includes(':')) {
            const parts = line.split(':');
            if (parts.length === 2) {
              const key = parts[0].trim();
              const value = parts[1].trim();
              if (key.length < 50 && value.length > 0) {
                tableRows.push([key, value]);
                i++;
                continue;
              }
            }
          }
          
          // Method 2: Sequential label-value pairs
          const isLabel = /^(Name|Gesamt\/Monat|Kaltmiete|Nebenkosten|Mietbeginn|Fläche|Miete\/m²|Status|Einheit|Adresse|Baujahr|Wohnfläche|Anzahl|Typ|Einzugsdatum|Mietdauer|Vertragsbeginn|Vertragsende)$/i.test(line.trim());
          
          if (isLabel && i + 1 < section.content.length) {
            const nextLine = section.content[i + 1];
            const isNextLabel = /^(Name|Gesamt\/Monat|Kaltmiete|Nebenkosten|Mietbeginn|Fläche|Miete\/m²|Status|Einheit|Adresse|Baujahr|Wohnfläche|Anzahl|Typ|Einzugsdatum|Mietdauer|Vertragsbeginn|Vertragsende)$/i.test(nextLine.trim());
            
            if (!isNextLabel && nextLine.trim().length > 0) {
              tableRows.push([line.trim(), nextLine.trim()]);
              i += 2;
              continue;
            }
          }
          
          // Otherwise it's a paragraph
          paragraphs.push(line);
          i++;
        }
        
        // Render tables
        if (tableRows.length > 0) {
          addDataTable(tableRows);
        }
        
        // Render paragraphs
        paragraphs.forEach(para => {
          if (para.trim()) {
            addText(para.trim(), 10, 'normal', [50, 50, 50]);
            yPosition += 2;
          }
        });
        
        yPosition += 3;
      });
      
      // === FOOTER ON ALL PAGES ===
      const totalPages = doc.internal.pages.length - 1;
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        const footerY = pageHeight - 10;
        
        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(150, 150, 150);
        doc.text('Generiert von Clara360 - Professionelle Immobilienverwaltung', margin, footerY);
        doc.text(`Seite ${i} von ${totalPages}`, pageWidth - margin - 25, footerY);
      }
      
      // Save PDF
      const fileName = `Bankreport_${selectedProperty.property_name.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
      console.log('[BANKREPORT] PDF downloaded successfully');
    } catch (error) {
      console.error('[BANKREPORT] Error generating PDF:', error);
      alert('Fehler beim Erstellen des PDFs. Bitte versuchen Sie es erneut.');
    }
  };

  // Progress steps configuration
  const progressSteps = [
    { icon: Database, text: 'Lade Objektdaten', subtext: 'Immobilie, Einheiten, Eigentümer' },
    { icon: Users, text: 'Lade Mieter & Verträge', subtext: 'Aktive Mietverhältnisse analysieren' },
    { icon: TrendingUp, text: 'Lade Finanzierungen', subtext: 'Kredite, LTV, DSCR berechnen' },
    { icon: Calculator, text: 'Berechne VPI-Anpassungen', subtext: 'Mietanpassungspotenzial ermitteln' },
    { icon: Sparkles, text: 'KI-Analyse läuft', subtext: 'Gemini 2.0 Flash generiert Insights' },
    { icon: Presentation, text: 'Erstelle Slides', subtext: 'Charts und Visualisierungen' },
    { icon: CheckCircle, text: 'Finalisiere', subtext: 'Präsentation wird vorbereitet' }
  ];

  return (
    <>
    <div className="min-h-screen bg-gray-50 dark:bg-black">
      {/* Header */}
      <div className="bg-white dark:bg-slate-900 shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/properties')}
                className="mr-4 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
                  <FileText className="w-8 h-8 mr-3 text-green-600" />
                  Bankreport erstellen
                </h1>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  Professionelle Immobilienanalyse für Banken und Investoren
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Property Selection Card */}
        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <Building className="w-5 h-5 mr-2 text-blue-600" />
            Objekt auswählen
          </h2>
          
          {loadingProperties ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="w-6 h-6 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600 dark:text-gray-400">Lade Objekte...</span>
            </div>
          ) : (
            <>
              <select
                value={selectedProperty?.id || ''}
                onChange={(e) => handlePropertySelect(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                disabled={loading}
              >
                <option value="">-- Bitte Objekt wählen --</option>
                {properties.map(prop => (
                  <option key={prop.id} value={prop.id}>
                    {prop.property_name} - {prop.city}
                    {prop.owners?.name && ` (Eigentümer: ${prop.owners.name})`}
                  </option>
                ))}
              </select>

              {/* Selected Property Info */}
              {selectedProperty && (
                <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white flex items-center mb-2">
                        <Building className="w-4 h-4 mr-2 text-blue-600" />
                        Objektdetails
                      </h3>
                      <div className="space-y-1 text-sm">
                        <p className="text-gray-700 dark:text-gray-300">
                          <span className="font-medium">Name:</span> {selectedProperty.property_name}
                        </p>
                        {selectedProperty.street && (
                          <p className="text-gray-700 dark:text-gray-300 flex items-center">
                            <MapPin className="w-3 h-3 mr-1" />
                            {selectedProperty.street}, {selectedProperty.postal_code} {selectedProperty.city}
                          </p>
                        )}
                        {selectedProperty.property_type && (
                          <p className="text-gray-700 dark:text-gray-300">
                            <span className="font-medium">Typ:</span> {selectedProperty.property_type}
                          </p>
                        )}
                        {selectedProperty.units && (
                          <p className="text-gray-700 dark:text-gray-300 flex items-center">
                            <Home className="w-3 h-3 mr-1" />
                            {selectedProperty.units} Einheiten
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Owner Info */}
                    {selectedProperty.owners && (
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white flex items-center mb-2">
                          <User className="w-4 h-4 mr-2 text-green-600" />
                          Eigentümerstruktur
                        </h3>
                        <div className="space-y-1 text-sm">
                          <p className="text-gray-700 dark:text-gray-300">
                            <span className="font-medium">Name:</span> {selectedProperty.owners.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            Eigentümer-ID: {selectedProperty.owners.id}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Report History Button */}
              {user && (
                <div className="mt-4">
                  <button
                    onClick={() => setShowHistoryModal(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 hover:border-blue-500/50 rounded-lg text-blue-400 font-medium transition-all"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Report-Historie anzeigen
                  </button>
                </div>
              )}

              {/* AI Provider Selector */}
              {selectedProperty && (
                <div className="mt-4 p-3 bg-gray-50 dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-gray-700">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <Sparkles className="w-4 h-4 inline mr-1" />
                    KI-Provider
                  </label>
                  <select
                    value={aiProvider}
                    onChange={(e) => setAiProvider(e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-black border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="qwen">⚡ Clara (Standard — lokal & sicher)</option>
                    <option value="gemini">🤖 Gemini (Standard)</option>
                    <option value="openai">🧠 OpenAI (Premium)</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {aiProvider === 'qwen' && 'Clara Standard — Deine Daten werden lokal verarbeitet. Schnell, sicher und kosteneffizient.'}
                    {aiProvider === 'gemini' && 'Google Gemini 2.0 Flash - Schnell & kosteneffizient'}
                    {aiProvider === 'openai' && 'OpenAI GPT-4.1 Mini - Premium-Qualität'}
                  </p>
                </div>
              )}

              {/* Generate Buttons */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Interactive Presentation Button (Primary) */}
                <button
                  onClick={handleGenerateInteractiveReport}
                  disabled={!selectedProperty || loading}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-6 py-4 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                >
                  {loading ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      Generiere Präsentation...
                    </>
                  ) : (
                    <>
                      <FileText className="w-5 h-5" />
                      🎬 Interaktive Präsentation
                    </>
                  )}
                </button>

                {/* Text Report Button (Secondary) */}
                <button
                  onClick={handleGenerateReport}
                  disabled={!selectedProperty || loading}
                  className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-6 py-4 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-all"
                >
                  {loading ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      Erstelle Report...
                    </>
                  ) : (
                    <>
                      <FileText className="w-5 h-5" />
                      📝 Text-Report
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6 flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-900 dark:text-red-200">Fehler</h3>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Report Display */}
        {report && (
          <>
            {/* Success Message */}
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6 flex items-start">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-green-900 dark:text-green-200">Bankreport erfolgreich erstellt</h3>
                <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                  Der Report wurde mit KI-Unterstützung generiert und enthält alle relevanten Kennzahlen.
                </p>
              </div>
            </div>

            {/* Metrics Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white dark:bg-slate-900 rounded-lg shadow p-4 border-l-4 border-blue-500">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Jahresmiete</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {parseFloat(report.metrics.yearlyRent).toLocaleString('de-DE')} €
                </p>
              </div>
              
              <div className="bg-white dark:bg-slate-900 rounded-lg shadow p-4 border-l-4 border-green-500">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Bruttorendite</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
                  {report.metrics.grossYield}%
                  <TrendingUp className="w-4 h-4 ml-2 text-green-500" />
                </p>
              </div>
              
              <div className="bg-white dark:bg-slate-900 rounded-lg shadow p-4 border-l-4 border-purple-500">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Auslastung</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {report.metrics.occupancyRate}%
                </p>
              </div>
              
              <div className="bg-white dark:bg-slate-900 rounded-lg shadow p-4 border-l-4 border-orange-500">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1 flex items-center">
                  <Users className="w-3 h-3 mr-1" />
                  Aktive Mieter
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {report.metrics.activeTenantCount} / {report.metrics.totalUnitCount}
                </p>
              </div>
            </div>

            {/* Report Content */}
            <div className="bg-white dark:bg-slate-900 rounded-lg shadow-md p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-green-600" />
                  Bankreport
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={handleDownloadPDF}
                    disabled={isGeneratingPDF}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg flex items-center gap-2 transition-colors"
                  >
                    {isGeneratingPDF ? (
                      <Loader className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    {isGeneratingPDF ? 'Generiere PDF...' : 'PDF Download'}
                  </button>
                  <button
                    onClick={handleDownloadReport}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg flex items-center gap-2 transition-colors"
                    title="Alternative PDF-Generierung (jsPDF)"
                  >
                    <Download className="w-4 h-4" />
                    jsPDF
                  </button>
                </div>
              </div>
              
              <div className="prose prose-sm max-w-none dark:prose-invert">
                <div 
                  className="text-gray-700 dark:text-gray-300 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: report.report }}
                />
              </div>
            </div>

            {/* Additional Metrics */}
            <div className="bg-white dark:bg-slate-900 rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Weitere Kennzahlen
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Kaltmiete (Monat)</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                    {parseFloat(report.metrics.totalColdRent).toLocaleString('de-DE')} €
                  </p>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Nebenkosten (Monat)</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                    {parseFloat(report.metrics.totalUtilities).toLocaleString('de-DE')} €
                  </p>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Ø Miete/m²</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                    {report.metrics.avgRentPerSqm} €
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>

    {/* Interactive Slides Overlay */}
    {showSlides && slides && (
      <BankReportSlides
        slides={slides}
        propertyName={selectedProperty?.property_name}
        reportData={{ slides, property: selectedProperty, metrics: report?.metrics }}
        propertyId={selectedProperty?.id}
        userId={user?.id}
        onClose={() => {
          setShowSlides(false);
          setSlides(null);
        }}
      />
    )}
    
    {/* PREMIUM PROGRESS MODAL - Enhanced with Step Indicators */}
    {loading && (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4 animate-fadeIn">
        <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl max-w-2xl w-full p-8 transform transition-all animate-scaleIn">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="text-5xl mb-4 animate-bounce">🚀</div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
              Bankreport wird erstellt
            </h2>
            <p className="text-base text-gray-600 dark:text-gray-400">
              Professionelle Immobilienanalyse mit KI-Unterstützung
            </p>
          </div>
          
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Gesamtfortschritt
              </span>
              <span className="text-lg font-bold text-purple-600 dark:text-purple-400">
                {progress}%
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-slate-800 rounded-full h-4 overflow-hidden shadow-inner relative">
              <div 
                className="h-full bg-gradient-to-r from-purple-600 via-blue-500 to-purple-600 rounded-full transition-all duration-700 ease-out relative overflow-hidden"
                style={{ width: `${progress}%` }}
              >
                {/* Shimmer effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" 
                     style={{ 
                       backgroundSize: '200% 100%',
                       animation: 'shimmer 2s infinite'
                     }}
                />
              </div>
            </div>
          </div>

          {/* Step-by-Step Indicators */}
          <div className="mb-8 space-y-3">
            {progressSteps.map((step, index) => {
              const StepIcon = step.icon;
              const isCompleted = index < currentStepIndex;
              const isCurrent = index === currentStepIndex;
              const isPending = index > currentStepIndex;
              
              return (
                <div 
                  key={index}
                  className={`flex items-start gap-4 p-4 rounded-xl transition-all duration-300 ${
                    isCurrent 
                      ? 'bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/30 dark:to-blue-900/30 border-2 border-purple-300 dark:border-purple-700 scale-105' 
                      : isCompleted
                      ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                      : 'bg-gray-50 dark:bg-slate-800/50 border border-gray-200 dark:border-gray-700 opacity-60'
                  }`}
                >
                  {/* Icon */}
                  <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                    isCurrent 
                      ? 'bg-purple-600 text-white animate-pulse' 
                      : isCompleted
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400'
                  }`}>
                    {isCompleted ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <StepIcon className="w-5 h-5" />
                    )}
                  </div>
                  
                  {/* Text */}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold ${
                      isCurrent 
                        ? 'text-gray-900 dark:text-white' 
                        : isCompleted
                        ? 'text-green-900 dark:text-green-200'
                        : 'text-gray-600 dark:text-gray-400'
                    }`}>
                      {step.text}
                      {isCompleted && ' ✓'}
                    </p>
                    <p className={`text-xs mt-1 ${
                      isCurrent 
                        ? 'text-gray-700 dark:text-gray-300' 
                        : 'text-gray-500 dark:text-gray-500'
                    }`}>
                      {step.subtext}
                    </p>
                  </div>
                  
                  {/* Status Badge */}
                  {isCurrent && (
                    <div className="flex-shrink-0">
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-purple-600 text-white text-xs font-medium">
                        <span className="inline-block w-1.5 h-1.5 bg-white rounded-full animate-ping"></span>
                        Läuft
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* AI Model Indicator */}
          {currentStepIndex === 4 && (
            <div className="mb-6 p-4 bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900/40 dark:to-blue-900/40 rounded-xl border-2 border-purple-300 dark:border-purple-700">
              <div className="flex items-center justify-center gap-3">
                <Sparkles className="w-6 h-6 text-purple-600 dark:text-purple-400 animate-pulse" />
                <div className="text-center">
                  <p className="text-sm font-bold text-purple-900 dark:text-purple-200">
                    KI-Modell: Gemini 2.0 Flash
                  </p>
                  <p className="text-xs text-purple-700 dark:text-purple-300 mt-1">
                    Generiert professionelle Insights und Empfehlungen
                  </p>
                </div>
                <Sparkles className="w-6 h-6 text-blue-600 dark:text-blue-400 animate-pulse" />
              </div>
            </div>
          )}
          
          {/* Estimated Time */}
          {estimatedTime > 0 && (
            <div className="flex items-center justify-center gap-3 text-base text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-slate-800 rounded-xl p-4">
              <span className="text-2xl">⏱️</span>
              <span>
                Noch ca. <span className="font-bold text-purple-600 dark:text-purple-400">{estimatedTime}</span> Sekunden
              </span>
            </div>
          )}
          
          {/* Loading Animation Dots */}
          <div className="flex justify-center gap-2 mt-6">
            <div className="w-3 h-3 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-3 h-3 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
        </div>
      </div>
    )}
    
    {/* Add custom animations to global CSS */}
    <style jsx>{`
      @keyframes shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes scaleIn {
        from { transform: scale(0.9); opacity: 0; }
        to { transform: scale(1); opacity: 1; }
      }
      .animate-fadeIn {
        animation: fadeIn 0.3s ease-out;
      }
      .animate-scaleIn {
        animation: scaleIn 0.3s ease-out;
      }
      .animate-shimmer {
        animation: shimmer 2s infinite;
      }
    `}</style>

    {/* Report History Modal */}
    {showHistoryModal && user && (
      <ReportHistory
        userId={user.id}
        onLoadReport={(data) => {
          if (data && data.report_data) {
            setSlides(data.report_data.slides || data.slides);
            setShowSlides(true);
            setShowHistoryModal(false);
          }
        }}
        onClose={() => setShowHistoryModal(false)}
      />
    )}
    </>
  );
}

